import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { StoreConfigurationRepositoryService } from 'src/app/core/services/store-configuration/store-configuration-repository.service';
import { Home } from '../models/home.model';
import { GoogleReviewResponse } from '../models/review.interface';
import { HomeApiService } from './home-api.service';

@Injectable({
  providedIn: 'root'
})
export class HomeRepositoryService {
  private homeApiService = inject(HomeApiService);
  private destroyRef = inject(DestroyRef);
  private storeConfigurationRepositoryService = inject(StoreConfigurationRepositoryService);

  // ===== OVERALL SERVICE STATE MANAGEMENT =====

  // ===== LOADING STATES =====
  private _isLoadingHomePage = signal<boolean>(false);
  readonly isLoadingHomePage = this._isLoadingHomePage.asReadonly();

  private _isLoadingGoogleReviews = signal<boolean>(false);
  readonly isLoadingGoogleReviews = this._isLoadingGoogleReviews.asReadonly();

  // ===== ERROR STATES =====

  // ===== DATA SIGNALS =====

  private _homePage = signal<Home | null>(null);
  readonly homePage = this._homePage.asReadonly();

  private _googleReviews = signal<GoogleReviewResponse | null>(null);
  readonly googleReviews = this._googleReviews.asReadonly();

  // ===== ENHANCED COMPUTED SIGNALS =====

  // Service state management removed - errors are handled at operation level

  // Overall Loading State
  readonly isAnyLoading = computed(() =>
    this._isLoadingHomePage() ||
    this._isLoadingGoogleReviews()
  );

  // Error handling removed - errors are handled at operation level

  // Data Availability
  readonly hasHomePage = computed(() => !!this._homePage());
  readonly hasGoogleReviews = computed(() => !!this._googleReviews());

  // Service is always healthy after auto-initialization

  // Individual Component Readiness
  readonly isHomePageReady = computed(() =>
    this.hasHomePage() && !this._isLoadingHomePage()
  );

  readonly isGoogleReviewsReady = computed(() =>
    this.hasGoogleReviews() && !this._isLoadingGoogleReviews()
  );


  // ===== ANGULAR 20 SERVICE AUTO-INITIALIZATION =====
  constructor() {

  }



  // ===== ENHANCED DATA LOADING METHODS =====

  async loadHomePage(): Promise<void> {
    // Set loading state
    this._isLoadingHomePage.set(true);

    try {
      const homePage = await firstValueFrom(
        this.homeApiService.loadHomePage().pipe(
          takeUntilDestroyed(this.destroyRef)
        )
      );
      this._homePage.set(homePage);
    } catch (error) {
      this._homePage.set(null);
    } finally {
      this._isLoadingHomePage.set(false);
    }
  }

  // Header and footer data loading methods removed - data now comes directly from HomePageRenderService



  // Header and footer related methods removed - data now comes directly from HomePageRenderService

  async loadGoogleReviews(): Promise<void> {
    // Set loading state
    this._isLoadingGoogleReviews.set(true);

    try {
      const googleReviews = await firstValueFrom(
        this.homeApiService.getGoogleReviews().pipe(
          takeUntilDestroyed(this.destroyRef)
        )
      );
      this._googleReviews.set(googleReviews);
    } catch (error) {
      this._googleReviews.set(null);
    } finally {
      this._isLoadingGoogleReviews.set(false);
    }
  }


  // Public setter methods for signals
  setHomePage(homePage: Home | null) {
    this._homePage.set(homePage);
  }

  setGoogleReviews(reviews: GoogleReviewResponse | null) {
    this._googleReviews.set(reviews);
  }

  // Clear methods
  clearHomePage() {
    this._homePage.set(null);
    this._isLoadingHomePage.set(false);
  }

  clearGoogleReviews() {
    this._googleReviews.set(null);
    this._isLoadingGoogleReviews.set(false);
  }


  // Utility methods for bulk operations
  clearAll() {
    this.clearHomePage();
    this.clearGoogleReviews();

  }

  // ===== UTILITY METHODS =====

  // Reset all errors
  clearAllErrors(): void {
    // Error handling removed - errors are handled at operation level
  }

  // Reset service state
  resetService(): void {
    this.clearAllErrors();
  }

  // Service is always safe to use after auto-initialization

  // Get current service status
  getServiceStatus(): {
    isLoading: boolean;
  } {
    return {
      isLoading: this.isAnyLoading()
    };
  }

  // Retry initialization
  async retryInitialization(): Promise<void> {
    this.resetService();
  }

  // Retry methods
  async retryLoadHomePage(): Promise<void> {
    await this.loadHomePage();
  }

  async retryLoadGoogleReviews(): Promise<void> {
    await this.loadGoogleReviews();
  }

  async retryAllFailed(): Promise<void> {
    // Retry loading all data
    await this.loadHomePage();
    await this.loadGoogleReviews();
  }

  // Getters for debugging and testing
  getCurrentState() {
    return {
      homePage: this._homePage(),
      googleReviews: this._googleReviews(),
      isLoadingHomePage: this._isLoadingHomePage(),
      isLoadingGoogleReviews: this._isLoadingGoogleReviews()
    };
  }
}
